<template>
  <Teleport :to="teleportTo ?? 'body'" :disabled="inline">
    <div v-if="modelValue"
class="modal-overlay"
:class="{ 'modal-overlay--inline': inline }"
:data-theme="light ? 'light' : undefined"
@mousedown="!inline && handleOverlayMouseDown($event)"
@mouseup="!inline && handleOverlayMouseUp($event)">
    <div
      ref="modalContent"
      class="modal-content"
      :class="{ 'modal-content--asking': inviterStep === 'ask' }"
      :data-theme="light ? 'light' : undefined"
      data-auth-mode="register"
      :role="inline ? undefined : 'dialog'"
      :aria-modal="inline ? undefined : true"
      :aria-label="$t('auth.register')"
      @keydown="handleDialogKeydown"
    >
      <button
        v-if="!inline"
        type="button"
        class="modal-close"
        :aria-label="$t('common.close')"
        @click="close"
      >
        <i class="zmdi zmdi-close" />
      </button>
      
      <!-- Step 1: Email and Referral -->
      <template v-if="step === 'email'">
        <h2 class="modal-title">{{ $t('auth.register') }}</h2>
        <p class="modal-subtitle">
          {{ $t('auth.registrationSubtitle') }}
        </p>

        <form @submit.prevent="handleEmailSubmit" class="auth-form">
          <!-- General error alert -->
          <div v-if="generalError" class="alert alert-error">
            <i class="zmdi zmdi-alert-circle" />
            <span>{{ generalError }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.email') }}</label>
            <div class="email-input-container">
              <BaseInput bare
                v-model="form.email" 
                type="email" 
                class="form-input" 
                :class="{ 'form-input--error': fieldErrors.email }"
                :placeholder="$t('auth.enterEmail')"
                required
                @focus="isEmailFocused = true"
                @blur="handleEmailBlur"
              />
              <EmailDomainSuggestions
                :email="form.email"
                :is-focused="isEmailFocused"
                @select="handleEmailSuggestionSelect"
              />
            </div>
            <span v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.inviterLogin') }}</label>
            <BaseInput bare
              ref="inviterInputRef"
              v-model="form.inviterLogin"
              type="text"
              class="form-input"
              :class="{
                'form-input--error': fieldErrors.inviterLogin,
                'form-input--highlight': inviterStep === 'input',
              }"
              :placeholder="$t('auth.enterInviterLogin')"
              :disabled="hasReferral"
              @input="onInviterInput"
            />
            <span v-if="inviterStep === 'input' && !fieldErrors.inviterLogin" class="form-hint">
              {{ $t('auth.enterInviterHint') }}
            </span>
            <span v-if="fieldErrors.inviterLogin" class="form-error">{{ fieldErrors.inviterLogin }}</span>
          </div>

          <div v-if="inviterStep === 'ask'" class="inviter-question">
            <p class="inviter-question__text">{{ $t('auth.hasInviterQuestion') }}</p>
            <div class="inviter-question__buttons">
              <BaseButton
                monochrome
                variant="ghost"
                type="button"
                class="btn btn-outline inviter-question__btn"
                :disabled="isLoading"
                @click="handleHasInviterNo"
              >
                {{ $t('auth.hasInviterNo') }}
              </BaseButton>
              <BaseButton monochrome
                type="button"
                class="btn btn-primary inviter-question__btn"
                :disabled="isLoading"
                @click="handleHasInviterYes"
              >
                {{ $t('auth.hasInviterYes') }}
              </BaseButton>
            </div>
          </div>

          <!-- Hidden Captcha Widget - initialized but not visible, triggered on Next -->
          <div class="captcha-hidden-container">
            <CaptchaWidget
              ref="captchaWidgetRef"
              @verified="onCaptchaStepVerified"
              @expired="onCaptchaExpired"
              @error="onCaptchaError"
            />
          </div>

          <BaseButton monochrome
            v-if="inviterStep !== 'ask'"
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
          >
            {{ isLoading ? $t('auth.loading') : $t('auth.next') }}
          </BaseButton>
        </form>

        <p class="modal-subtitle">{{ $t('emailLink.telegramLogin') }}</p>
        <SocialAuthButtons
          mode="login"
          :light="light"
          :inviter-login="form.inviterLogin"
          @totp-required="(signal) => emit('switch-to-login-totp', signal)"
        />

        <div class="auth-footer">
          <p>{{ $t('auth.hasAccount') }} <a href="#" @click.prevent="switchToLogin">{{ $t('auth.login') }}</a></p>
        </div>

        <div class="legal-text" v-html="$t('auth.legalDisclaimer', { privacy: '<a href=\'/app/law/policy\' target=\'_blank\'>' + $t('auth.privacyPolicy') + '</a>', terms: '<a href=\'/app/law/terms\' target=\'_blank\'>' + $t('auth.termsOfUse') + '</a>' })" />
      </template>

      <!-- Step 2: Create Password -->
      <template v-else-if="step === 'password'">
        <h2 class="modal-title">{{ $t('auth.createPassword') }}</h2>
        <p class="modal-subtitle">
          {{ $t('auth.createPasswordSubtitle') }}
        </p>

        <form @submit.prevent="handlePasswordSubmit" class="auth-form">
          <div class="form-group">
            <label class="form-label">{{ $t('auth.emailAddress') }}</label>
            <div class="email-display">
              <span>{{ form.email }}</span>
              <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.edit') }}</a>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.password') }}</label>
            <div class="password-input-wrapper">
              <BaseInput bare
                ref="passwordInput"
                v-model="form.password" 
                :type="showPassword ? 'text' : 'password'" 
                class="form-input" 
                :placeholder="$t('auth.enterPassword')"
                autocomplete="new-password"
                required
              />
              <button type="button"
                class="toggle-password"
                tabindex="-1"
                @click="showPassword = !showPassword">
                <i :class="showPassword ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
              </button>
            </div>
            
            <!-- Password requirements checklist -->
            <div class="password-requirements" v-if="form.password.length > 0">
              <div class="requirement" :class="{ valid: passwordValidation.minLength }">
                <i :class="passwordValidation.minLength ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.minLength') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasUppercase }">
                <i :class="passwordValidation.hasUppercase ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.uppercase') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasLowercase }">
                <i :class="passwordValidation.hasLowercase ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.lowercase') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasDigit }">
                <i :class="passwordValidation.hasDigit ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.digit') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasSpecial }">
                <i :class="passwordValidation.hasSpecial ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.special') }}</span>
              </div>
            </div>
          </div>

          <div v-if="generalError" class="alert alert-error">
            <i class="zmdi zmdi-alert-circle" />
            <span>{{ generalError }}</span>
          </div>

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading || !isPasswordValid">
            {{ isLoading ? $t('auth.creating') : $t('auth.continue') }}
          </BaseButton>
        </form>

        <div class="auth-footer auth-footer--links">
          <a href="/app/law/terms" target="_blank">{{ $t('auth.termsOfUse') }}</a>
          <span class="separator">|</span>
          <a href="/app/law/privacy" target="_blank">{{ $t('auth.privacyPolicy') }}</a>
        </div>
      </template>
    </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import { computed, nextTick, reactive, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useModalOverlay } from '@/composables/useModalOverlay'
import { useRouter } from 'vue-router'
import { parseApiError } from '@/shared/utils/apiErrorParser'
import { isEmailAlreadyExistsError } from '@/shared/utils/captchaRetryService'
import CaptchaWidget from '@/shared/components/CaptchaWidget.vue'
import SocialAuthButtons from './SocialAuthButtons.vue'
import type { TelegramTotpSignal } from '../services/TelegramLoginPollingService'
import EmailDomainSuggestions from '@/shared/components/EmailDomainSuggestions.vue'
import { getCaptchaFieldName } from '@/shared/utils/captchaConfig'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  light?: boolean
  inline?: boolean
  teleportTo?: HTMLElement
  initialEmail?: string
}>()

const modalContent = ref<HTMLElement>()
const focus = () => modalContent.value?.querySelector<HTMLInputElement>('input[type="email"]')?.focus({ preventScroll: true })
defineExpose({ focus })
function handleDialogKeydown(event: KeyboardEvent) {
  if (props.inline) return
  if (event.key === 'Escape') {
    event.stopPropagation()
    close()
    return
  }
  if (event.key !== 'Tab' || !modalContent.value) return
  const elements = [...modalContent.value.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), [tabindex="0"]')]
    .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0)
  const first = elements[0]
  const last = elements[elements.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first?.focus()
  }
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'switch-to-login'): void
  (e: 'switch-to-login-with-code', email: string): void
  /** OTP-R6c: Telegram дал pending-вход TOTP-аккаунта — шаг кода в окне входа. */
  (e: 'switch-to-login-totp', signal: TelegramTotpSignal): void
}>()

const profileStore = useProfileStore()
const router = useRouter()

// Two-step registration flow: email -> password (captcha shown as popup)
type Step = 'email' | 'password'
const step = ref<Step>('email')
const captchaWidgetRef = ref<InstanceType<typeof CaptchaWidget> | null>(null)

const form = reactive({
  email: props.initialEmail ?? '',
  inviterLogin: '',
  password: ''
})

const hasReferral = ref(false)
const isLoading = ref(false)
const storedReferralCode = ref<string | null>(null)
const referralId = ref<number | null>(null)
const fieldErrors = ref<Record<string, string>>({})
const generalError = ref<string | null>(null)
const showPassword = ref(false)
const passwordInput = ref<HTMLInputElement | null>(null)
const isEmailFocused = ref(false)

// Inline inviter-question flow: after clicking Next without an inviter, we ask
// "Do you have an inviter login?" with Yes/No buttons. Yes → highlight the
// inviter field + hint; No → proceed straight to captcha.
type InviterStep = 'idle' | 'ask' | 'input'
const inviterStep = ref<InviterStep>('idle')
const inviterInputRef = ref<HTMLInputElement | null>(null)

const handleEmailSuggestionSelect = (email: string) => {
  form.email = email
  // Note: suggestions will hide automatically because selected email has a complete domain
}

const handleEmailBlur = () => {
  // Small delay to allow click on suggestion to process before hiding
  setTimeout(() => {
    isEmailFocused.value = false
  }, 150)
}

const clearErrors = () => {
  fieldErrors.value = {}
  generalError.value = null
}

// reCAPTCHA state
const captchaToken = ref('')

// Track which action triggered captcha
type CaptchaAction = 'email' | null
const pendingCaptchaAction = ref<CaptchaAction>(null)

// Handler for captcha step - proceeds to password after verification
const onCaptchaStepVerified = async (token: string) => {
  captchaToken.value = token
  
  const action = pendingCaptchaAction.value
  pendingCaptchaAction.value = null // Reset after use

  if (action !== 'email') {
    console.log('[GonkaGame] Registration captcha verified silently, token stored')
    return
  }
  
  // Validate inviter if provided
  if (form.inviterLogin) {
    isLoading.value = true
    try {
      const sponsorRes = await profileStore.searchSponsor({ login: form.inviterLogin }) as any
      if (sponsorRes.result !== 'success') {
        fieldErrors.value.inviterLogin = t('auth.inviterNotFound')
        step.value = 'email' // Go back to email step
        isLoading.value = false
        return
      }
      referralId.value = sponsorRes.rid
      console.log('[GonkaGame][Registration] Sponsor found, referralId set to:', referralId.value)
    } catch {
      step.value = 'email'
      isLoading.value = false
      return
    }
    isLoading.value = false
  }
  
  // Proceed to password step
  step.value = 'password'
  nextTick(() => passwordInput.value?.focus())
}

const onCaptchaExpired = () => {
  captchaToken.value = ''
}

const onCaptchaError = () => {
  captchaToken.value = ''
  generalError.value = t('auth.captchaError')
}

// Password validation
const passwordValidation = computed(() => {
  const pwd = form.password
  return {
    minLength: pwd.length >= 8,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasDigit: /[0-9]/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
  }
})

const isPasswordValid = computed(() => {
  const v = passwordValidation.value
  return v.minLength && v.hasUppercase && v.hasLowercase && v.hasDigit && v.hasSpecial
})

onMounted(async () => {
  // Check for partner_referral_code in localStorage first
  storedReferralCode.value = localStorage.getItem('partner_referral_code')
  
  // Extract referral from cookie if exists
  const getCookieValue = (name: string): string | null => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
  }
  const cookieReferral = getCookieValue('referal')
  
  // Use localStorage value first, then fallback to cookie
  const referralCode = storedReferralCode.value || cookieReferral
  
  // If we have a referral code from either source, pre-fill the field
  if (referralCode) {
    // Pre-fill the field immediately so user sees it right away
    form.inviterLogin = referralCode
    
    try {
      const sponsorRes = await profileStore.searchSponsor({ login: referralCode }) as any
      if (sponsorRes.result === 'success') {
        referralId.value = sponsorRes.rid
        hasReferral.value = true
        // Sync to localStorage if came from cookie
        if (!storedReferralCode.value && cookieReferral) {
          localStorage.setItem('partner_referral_code', cookieReferral)
          storedReferralCode.value = cookieReferral
        }
      } else {
        // Invalid referral, clear it but keep the field value for user to see/edit
        localStorage.removeItem('partner_referral_code')
        storedReferralCode.value = null
        hasReferral.value = false
      }
    } catch (error: unknown) {
      // An unavailable API does not invalidate the visitor's referral link.
      generalError.value = parseApiError(error).message
      hasReferral.value = false
    }
  } else {
    hasReferral.value = false
  }
})

const close = () => {
  emit('update:modelValue', false)
  resetState()
}

const resetState = () => {
  step.value = 'email'
  form.email = ''
  form.inviterLogin = ''
  form.password = ''
  inviterStep.value = 'idle'
  referralId.value = null
  hasReferral.value = false
  clearErrors()
}

// Modal overlay click handling (prevents accidental close on text selection)
const { handleOverlayMouseDown, handleOverlayMouseUp } = useModalOverlay(close)

const goBackToEmail = () => {
  step.value = 'email'
  clearErrors()
}

// Step 1: Validate email and referral, then trigger captcha popup
const handleEmailSubmit = async () => {
  clearErrors()

  // Basic email validation before showing captcha
  if (!form.email || !form.email.includes('@')) {
    fieldErrors.value.email = t('auth.enterEmail')
    return
  }

  // No inviter provided and none remembered — ask inline with Yes/No.
  // If the user already answered "Yes" (inviterStep === 'input') but left the
  // field empty, show an error instead of re-asking the question.
  if (!hasReferral.value && !form.inviterLogin.trim()) {
    if (inviterStep.value === 'input') {
      fieldErrors.value.inviterLogin = t('auth.inviterRequired')
      return
    }
    inviterStep.value = 'ask'
    return
  }

  proceedToCaptcha()
}

const proceedToCaptcha = () => {
  // Set pending action and trigger Geetest popup (bind mode)
  pendingCaptchaAction.value = 'email'
  captchaWidgetRef.value?.showCaptcha?.()
}

const handleHasInviterYes = () => {
  inviterStep.value = 'input'
  nextTick(() => inviterInputRef.value?.focus())
}

const handleHasInviterNo = () => {
  form.inviterLogin = ''
  referralId.value = null
  inviterStep.value = 'idle'
  proceedToCaptcha()
}

const onInviterInput = () => {
  if (fieldErrors.value.inviterLogin) {
    fieldErrors.value = { ...fieldErrors.value, inviterLogin: '' }
  }
}

// Step 2: Complete registration with password
const handlePasswordSubmit = async () => {
  if (!form.password || !isPasswordValid.value) return

  isLoading.value = true
  clearErrors()

  try {
    const payload: Record<string, unknown> = {
      email: form.email,
      password: form.password,
      [getCaptchaFieldName()]: captchaToken.value
    }
    
    // Include referral only if we have a valid sponsor ID
    if (referralId.value) {
      payload.referral = referralId.value
    }
    
    
    await profileStore.register(payload)
    
    // Clear stored referral code after successful registration
    localStorage.removeItem('partner_referral_code')
    
    // Navigate first, then close modal (order matters for SPA navigation)
    console.log('[GonkaGame][Registration] Success! Navigating to dashboard...')
    await router.push({ name: 'dashboard' })
    console.log('[GonkaGame][Registration] Navigation complete, closing modal')
    close()
  } catch (error: any) {
    // Check if email already exists - switch to login modal with code
    if (isEmailAlreadyExistsError(error)) {
      // Show iziToast and emit for parent to handle
      ;(window as any).iziToast?.info({
        title: t('auth.emailRegisteredToast'),
        message: t('auth.sendingCodeMessage'),
        position: 'topRight',
        timeout: 3000
      })
      // Emit with email for parent to pass to LoginModal
      emit('switch-to-login-with-code', form.email)
      return
    }
    
    const parsed = parseApiError(error)
    
    // Set field-specific errors
    if (Object.keys(parsed.fieldErrors).length > 0) {
      fieldErrors.value = parsed.fieldErrors
    }
    
    // Show general error message
    generalError.value = parsed.message
  } finally {
    isLoading.value = false
  }
}

const switchToLogin = () => {
  close()
  emit('switch-to-login')
}
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--ds-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  // Keep the SDK's sibling captcha layer out of backdrop-filter compositing.
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}

.modal-overlay--inline {
  position: relative;
  inset: auto;
  width: 100%;
  height: auto;
  display: block;
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  .modal-content {
    margin: 0 auto;
    max-height: none;
    overflow: visible;
  }
}

.modal-content {
  background: var(--ds-surface);
  border-radius: var(--ds-radius-modal);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  position: relative;
  box-shadow: 0 20px 40px var(--ds-scrim);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: var(--ds-text-size-h-md);
  color: var(--ds-text-muted);
  cursor: pointer;
  
  &:hover {
    color: var(--ds-text-secondary);
  }
}

.modal-title {
  font-family: var(--ds-font-display);
  font-size: var(--p-text-28);
  font-weight: var(--ds-weight-bold);
  letter-spacing: -0.02em;
  text-align: center;
  margin-bottom: 12px;
  color: var(--ds-text);
}

.modal-subtitle {
  text-align: center;
  color: var(--ds-text-secondary);
  margin-bottom: 32px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.email-input-container {
  position: relative;
}

.form-label {
  display: block;
  font-weight: var(--ds-weight-semibold);
  margin-bottom: 8px;
  color: var(--ds-text);
}

.form-input {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid var(--ds-border);
  border-radius: var(--p-radius-50);
  background: var(--ds-overlay-04);
  color: var(--ds-text);
  font-size: var(--ds-text-size-body-lg);
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: var(--ds-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    box-shadow: var(--ds-focus-ring-shadow);
  }
}

.btn {
  padding: 14px;
  border-radius: var(--p-radius-50);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body-lg);
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block {
  width: 100%;
  display: block;
}

.btn-primary {
  background: linear-gradient(180deg, var(--ds-accent-light), var(--ds-accent));
  color: var(--ds-text-on-accent);
  box-shadow: var(--ds-glow-cta);

  &:hover {
    transform: translateY(-2px);
  }
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--ds-border);
  color: var(--ds-text);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;

  &:hover {
    background: var(--ds-surface-raised);
  }
}

.social-buttons {
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: center;
}

.captcha-fallback {
  margin: 20px 0;
  padding: 16px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  background: var(--ds-surface-raised);
  text-align: center;

  &__title {
    font-weight: var(--ds-weight-semibold);
    margin-bottom: 12px;
    color: var(--ds-text);
  }

  &__widget {
    display: inline-block;
  }

  &__hint {
    margin-top: 12px;
    font-size: var(--p-text-13);
    color: var(--ds-text-muted);
  }
}

.social-btn {
  width: 60px;
  height: 48px;
  border-radius: var(--p-radius-50);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--ds-surface-raised);
  }
}

.social-btn--wide {
  width: 100%;
  gap: 10px;
  padding: 0 16px;
}

.social-btn--wide span {
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
}

.social-btn.telegram {
  color: var(--ds-text-secondary);

  .telegram-icon {
    width: 18px;
    height: 18px;
    color: #0088cc;
  }
}

.divider {
  margin: 24px 0;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: var(--ds-border);
    z-index: 0;
  }

  span {
    background: var(--ds-surface);
    padding: 0 12px;
    color: var(--ds-text-muted);
    position: relative;
    z-index: 1;
  }
}


.auth-footer {
  text-align: center;
  margin-top: 24px;
  color: var(--ds-text-secondary);
  
  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-semibold);
  }
}

.legal-text {
  margin-top: 32px;
  font-size: var(--ds-text-size-caption);
  color: var(--ds-text-muted);
  text-align: center;
  line-height: 1.4;

  a {
    color: var(--ds-accent);
    text-decoration: none;
  }
}

.alert {
  padding: 12px 16px;
  border-radius: var(--ds-radius-card);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--ds-text-size-body);
  line-height: 1.4;
  
  &.alert-warning {
    background: var(--ds-warning-soft);
    color: var(--ds-warning);
    border: 1px solid var(--ds-warning-soft);
  }
  
  &.alert-error {
    background: var(--ds-danger-soft);
    color: var(--ds-danger);
    border: 1px solid var(--ds-danger-soft);
  }
  
  i {
    font-size: var(--ds-text-size-h-sm);
  }
}

.form-error {
  display: block;
  color: var(--ds-danger);
  font-size: var(--ds-text-size-caption);
  margin-top: 4px;
}

.form-input--error {
  border-color: var(--ds-danger) !important;

  &:focus {
    border-color: var(--ds-danger) !important;
  }
}

.form-input--highlight {
  border-color: var(--ds-accent) !important;
  box-shadow: 0 0 0 3px var(--ds-accent-soft-15);
}

.form-hint {
  display: block;
  color: var(--ds-accent);
  font-size: var(--ds-text-size-caption);
  margin-top: 6px;
}

.inviter-question {
  position: relative;
  z-index: 2;
  margin-bottom: 20px;
  padding: 20px;
  background: var(--ds-surface);
  border: 1px solid var(--ds-border);
  border-radius: var(--p-radius-14);
  text-align: center;
  box-shadow: 0 12px 30px var(--ds-accent-soft-12);

  &__text {
    margin: 0 0 16px;
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-semibold);
    color: var(--ds-text);
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }

  &__btn {
    flex: 1;
  }
}

// Focus mode: when asking the inviter question, blur/dim every other element
// in the modal so the user's attention is locked on the Yes/No prompt. The
// close button stays fully visible so the user can still dismiss the modal.
.modal-content--asking {
  .modal-title,
  .modal-subtitle,
  .auth-form > *:not(.inviter-question),
  :deep(.social-buttons),
  .auth-footer,
  .legal-text {
    opacity: 0.35;
    filter: blur(2px);
    pointer-events: none;
    user-select: none;
    transition: opacity 0.2s ease, filter 0.2s ease;
  }
}

// Password step styles
.email-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-card);
  
  span {
    color: var(--ds-text);
    font-weight: var(--ds-weight-medium);
  }
  
  a {
    color: var(--ds-accent);
    font-size: var(--ds-text-size-body);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.password-input-wrapper {
  position: relative;
  
  .form-input {
    padding-right: 48px;
  }
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--ds-text-muted);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: var(--ds-text-secondary);
  }
  
  i {
    font-size: var(--ds-text-size-h-sm);
  }
}

.password-requirements {
  margin-top: 12px;
  padding: 12px;
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-control);
  
  .requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--p-text-13);
    color: var(--ds-text-muted);
    padding: 4px 0;
    
    i {
      font-size: var(--ds-text-size-body-lg);
    }
    
    &.valid {
      color: var(--ds-success);
    }
  }
}

.auth-footer--links {
  display: flex;
  justify-content: center;
  gap: 16px;
  
  .separator {
    color: #ccc;
  }
  
  a {
    color: var(--ds-accent);
    font-size: var(--ds-text-size-body);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

/* Hide captcha widget - bind mode shows popup on showCaptcha() call */
.captcha-hidden-container {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
